// src/v2/hooks/useReportMCRF.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useReportMCRF = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Scenario A: Download the raw Pag-IBIG template directly from public/docs/
   */
  const downloadBlankTemplate = useCallback((month, year) => {
    const pdfUrl = `${window.location.origin}/docs/PagIBIG_MCRF.pdf`;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `PagIBIG_MCRF_${month}_${year}_Blank.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showGeneralMessage("Blank template downloaded (No records found).", true);
  }, [showGeneralMessage]);

  /**
   * Scenario B: Fill the Pag-IBIG MCRF with data using pdf-lib
   */
  const fillAndDownloadPDF = useCallback(async (data, month, year, companyInfo) => {
    try {
      const pdfUrl = `${window.location.origin}/docs/PagIBIG_MCRF.pdf`;
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Template not found");

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const page = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // --- COORDINATE MAPPING (Estimates for Pag-IBIG MCRF) ---
      // 1. Employer Header
      page.drawText(companyInfo?.name || "", { x: 100, y: 720, size: 10, font });
      page.drawText(companyInfo?.tin || "", { x: 100, y: 700, size: 10, font });

      // 2. Applicable Period
      page.drawText(`${month} ${year}`, { x: 480, y: 720, size: 10, font });

      // 3. Totals Example
      const totalEE = data.reduce((sum, item) => sum + (item.eeAmount || 0), 0);
      const totalER = data.reduce((sum, item) => sum + (item.erAmount || 0), 0);
      const grandTotal = totalEE + totalER;

      page.drawText(grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: 500, y: 300, size: 10, font });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PagIBIG_MCRF_${month}_${year}.pdf`;
      link.click();

      showGeneralMessage("MCRF generated and downloaded.", true);
    } catch (error) {
      console.error(error);
      showGeneralMessage("Error filling PDF. Downloading blank instead.", false);
      downloadBlankTemplate(month, year);
    }
  }, [showGeneralMessage, downloadBlankTemplate]);

  const generateAndDownload = useCallback(async (params, month, year, companyInfo) => {
    setLoading(true);
    try {
      let data = [];
      try {
        const res = await reportsService.getReportData('MCRF', params);
        data = res || [];
      } catch (apiErr) {
        console.warn("Backend not found. Falling back to Demo Mode.");
        data = [];
      }

      if (data.length === 0) {
        downloadBlankTemplate(month, year);
      } else {
        await fillAndDownloadPDF(data, month, year, companyInfo);
      }
    } catch (err) {
      showGeneralMessage("Failed to process request.", false);
    } finally {
      setLoading(false);
    }
  }, [fillAndDownloadPDF, downloadBlankTemplate, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};